import { RotateCcw, Waves } from 'lucide-react';
import { useMemo, useState } from 'react';

const presets = [
  { label: '初始', value: 12 },
  { label: '变大', value: 52 },
  { label: '可能改道', value: 92 }
];

function getRiverPath(stage: number) {
  const leftPull = 52 + stage * 0.48;
  const centerPush = 312 + stage * 0.52;
  const neck = 520 - stage * 0.82;
  const upperLift = 56 - stage * 0.16;
  const lowerDrop = 245 + stage * 0.18;

  return `M35 198
    C${110 + leftPull} ${upperLift}, 205 ${38 + stage * 0.1}, 306 ${136}
    C400 ${238}, ${neck} ${lowerDrop}, ${585} ${132}
    C668 ${48}, ${744 - stage * 0.1} 78, 790 122`;
}

function getCutoffPath(stage: number) {
  const shift = Math.max(0, stage - 60);
  return `M336 ${146 + shift * 0.1} C408 ${122 - shift * 0.14}, 468 ${132 - shift * 0.08}, 538 ${128}`;
}

function getStageText(stage: number) {
  if (stage < 28) {
    return '河道刚开始偏转。外侧水流更快，内侧已经能留下浅浅的泥沙。';
  }
  if (stage < 66) {
    return '弯道变宽，凹岸继续被冲刷，凸岸慢慢长出沙洲和草带。';
  }
  return '河弯接近到只剩窄窄的脖子。洪水期可能冲开短路，旧河弯会留下湿地或牛轭湖。';
}

function getActivePreset(stage: number) {
  if (stage < 28) return '初始';
  if (stage < 66) return '变大';
  return '可能改道';
}

export function InteractiveMeander() {
  const [stage, setStage] = useState(42);
  const riverPath = useMemo(() => getRiverPath(stage), [stage]);
  const cutoffPath = useMemo(() => getCutoffPath(stage), [stage]);
  const activePreset = getActivePreset(stage);

  return (
    <section className="meander-lab meander-atlas" aria-label="Interactive meander diagram">
      <div className="panel-heading">
        <Waves aria-hidden="true" />
        <div>
          <h2>动一动：河弯是怎样长大的？</h2>
          <p>拖动时间滑杆，看凹岸冲刷、凸岸堆积，河道怎样慢慢改变。</p>
        </div>
      </div>

      <div className="meander-grid meander-atlas-grid">
        <div className="river-board river-atlas-board">
          <svg viewBox="0 0 820 360" role="img" aria-label="河流弯曲过程示意">
            <defs>
              <linearGradient id="grassGradient" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="#e3f0d1" />
                <stop offset="52%" stopColor="#f4edca" />
                <stop offset="100%" stopColor="#d5e7c0" />
              </linearGradient>
              <linearGradient id="riverGradient" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#8ed1db" />
                <stop offset="45%" stopColor="#2c86a6" />
                <stop offset="100%" stopColor="#81c9d9" />
              </linearGradient>
              <filter id="riverShadow" x="-15%" y="-20%" width="130%" height="150%">
                <feDropShadow dx="0" dy="12" floodColor="#184d5c" floodOpacity=".18" stdDeviation="10" />
              </filter>
              <pattern id="meadowTexture" height="18" patternUnits="userSpaceOnUse" width="18">
                <path d="M3 16 C7 10, 11 10, 15 4" fill="none" stroke="#8fa966" strokeOpacity=".16" strokeWidth="1.4" />
              </pattern>
            </defs>

            <rect fill="url(#grassGradient)" height="360" width="820" />
            <rect fill="url(#meadowTexture)" height="360" width="820" />
            <path className="terrain-line" d="M-20 78 C112 42, 190 82, 292 57 C416 28, 520 61, 646 34 C736 17, 790 33, 850 18" />
            <path className="terrain-line soft" d="M-30 286 C88 250, 205 292, 318 266 C428 240, 552 282, 670 245 C748 221, 790 232, 848 208" />
            <path className="old-channel" d="M345 146 C410 244, 490 246, 580 134" opacity={stage > 62 ? 0.7 : 0.22} />
            <path className="cutoff-channel" d={cutoffPath} opacity={stage > 72 ? 0.82 : 0.18} />
            <path className="river-bank" d={riverPath} />
            <path className="river-channel" d={riverPath} filter="url(#riverShadow)" />
            <path className="river-highlight" d={riverPath} />
            <path className="river-current" d={riverPath} />

            <ellipse className="sandbar" cx={235 + stage * 0.18} cy={161 + stage * 0.1} rx="54" ry="18" transform="rotate(-15 250 170)" />
            <ellipse className="sandbar second" cx={497 - stage * 0.18} cy={222 - stage * 0.05} rx="65" ry="20" transform="rotate(12 492 220)" />
            <path className="reed-cluster" d="M154 236 l-8 -28 M170 235 l2 -31 M186 239 l12 -29 M624 214 l-4 -31 M641 214 l10 -31 M659 219 l18 -26" />
          </svg>

          <span className="river-pin concave">
            <b>凹岸</b>
            水流更急，岸边被冲刷
          </span>
          <span className="river-pin convex">
            <b>凸岸</b>
            水慢下来，泥沙堆积
          </span>
          <span className="river-pin oxbow">
            <b>旧河道</b>
            可能留下湿地或牛轭湖
          </span>
        </div>

        <div className="lab-controls time-panel">
          <label htmlFor="meander-stage">时间推移</label>
          <div className="meander-meter" aria-hidden="true">
            <span style={{ width: `${stage}%` }} />
          </div>
          <div className="stage-buttons" aria-label="River stage presets">
            {presets.map((preset) => (
              <button
                className={preset.label === activePreset ? 'active' : ''}
                key={preset.label}
                onClick={() => setStage(preset.value)}
                type="button"
              >
                {preset.label}
              </button>
            ))}
          </div>
          <input
            id="meander-stage"
            max="100"
            min="0"
            onChange={(event) => setStage(Number(event.target.value))}
            type="range"
            value={stage}
          />
          <p>{getStageText(stage)}</p>
          <button className="reset-button" onClick={() => setStage(42)} type="button">
            <RotateCcw aria-hidden="true" />
            回到中间阶段
          </button>
        </div>
      </div>
    </section>
  );
}
